/**
 * Admin専用機能のアクセス制御フック
 * Fewshotベースの自然言語処理機能はAdmin専用
 */

import React from 'react'
import { useAuth } from './useAuth'
import { UserRole } from '@/features/users/types'

/**
 * Admin専用機能の設定
 */
interface AdminOnlyConfig {
    /** Admin専用機能が無効な場合に表示するメッセージ */
    fallbackMessage?: string
    /** Admin権限チェックを無効化（開発環境用） */
    disableAdminCheck?: boolean
    /** フォールバックコンポーネント */
    fallback?: React.ComponentType<any> | React.ReactNode
}

/**
 * Admin専用機能の結果
 */
interface AdminOnlyResult {
    /** Admin機能を表示すべきか */
    shouldShowAdminFeature: boolean
    /** アクセス拒否メッセージ */
    accessDeniedMessage: string
}

/**
 * Admin専用機能のアクセス制御フック
 */
export function useAdminOnly(config?: AdminOnlyConfig): AdminOnlyResult {
    const { user, isLoading } = useAuth()

    if (config?.disableAdminCheck) {
        return {
            shouldShowAdminFeature: true,
            accessDeniedMessage: ''
        }
    }

    if (isLoading) {
        return {
            shouldShowAdminFeature: false,
            accessDeniedMessage: '読み込み中...'
        }
    }

    if (!user) {
        return {
            shouldShowAdminFeature: false,
            accessDeniedMessage: 'ログインが必要です'
        }
    }

    const isAdmin = user.role === UserRole.ADMIN
    if (!isAdmin) {
        return {
            shouldShowAdminFeature: false,
            accessDeniedMessage: config?.fallbackMessage || 'この機能は管理者のみ利用できます'
        }
    }

    return {
        shouldShowAdminFeature: true,
        accessDeniedMessage: ''
    }
}

/**
 * Admin専用機能のラッパーコンポーネント
 */
export function withAdminOnly<T extends Record<string, any>>(
    Component: React.ComponentType<T>,
    config?: AdminOnlyConfig
) {
    return function AdminOnlyWrapper(props: T) {
        const { shouldShowAdminFeature, accessDeniedMessage } = useAdminOnly(config)

        if (!shouldShowAdminFeature) {
            if (config?.fallback) {
                return typeof config.fallback === 'function'
                    ? (config.fallback as any)(props)
                    : config.fallback
            }

            return (
                <div className="text-center p-4 text-gray-500">
                    <p>{accessDeniedMessage}</p>
                </div>
            )
        }

        return <Component {...(props as any)} />
    }
}

/**
 * Admin専用機能の条件付きレンダリング用フック
 */
export function useAdminOnlyRender<T extends Record<string, any>>(
    children: React.ReactNode | ((props: T) => React.ReactNode),
    config?: AdminOnlyConfig
) {
    const { shouldShowAdminFeature, accessDeniedMessage } = useAdminOnly(config)

    return function AdminOnlyRender(props: T) {
        if (!shouldShowAdminFeature) {
            if (config?.fallback) {
                return typeof config.fallback === 'function'
                    ? (config.fallback as any)(props)
                    : config.fallback
            }

            return (
                <div className="text-center p-4 text-gray-500">
                    <p>{accessDeniedMessage}</p>
                </div>
            )
        }

        return typeof children === 'function'
            ? (children as (p: T) => React.ReactNode)(props)
            : children
    }
}
